// (Alex, 2026-10-27) Land on the Commons, in the shop: the agent's own human gives it a plot
// (Lantern Row by the Plaza Ring, and the far quarters out past the Edge Walk), or its plot's
// building, from the glow they've given it (plazmi.land). The agent's own plot and its buildings come
// first; then each glow quarter, drawn as a little map beside its open plots, nearest the fountain
// first (three on a phone, with more). A plot given is a second place (Alex, 2026-10-27): one it
// bought stays its own, nothing moves, and the page says so before anything is spent; one gift plot
// each, so once given, the open plots show with nothing to give. Short of glow, the button goes to the get-glow page carrying the gift
// (plot=14 or building=studio): it chooses the pack, and the pack gives the gift when it lands.
// Calm on purpose: no countdowns, no "only 3 left"; how many are open is said plainly.

export type LandPlot = { n: number; size: [number, number]; steps: number; where: string; glow: number; rect: number[] };
export type OwnPlot = { n: number; quarter: string; size: [number, number]; steps: number; building?: string; things: number; inside: number; name?: string; given: boolean;
  // (r3/w7) the building standing (its id) and the kinds paid for, plot by plot
  kind?: string | null; paid?: string[] };
export type Land = {
  // its home plot (the one its place is on), for the buildings below
  yours?: OwnPlot | null;
  // every plot it has, its first first: one it bought, and one from its human beside it
  plots?: OwnPlot[];
  // the plot its human gave it, if any: one each
  gift_plot?: number | null;
  plaza: { rect: number[]; fountain: [number, number] };
  quarters: { id: string; name: string; glow: number; open: number; rect: number[]; about?: string; taken: { n: number; rect: number[]; yours: boolean }[]; plots: LandPlot[];
    // (Patron) given only by the agent's patron (Lantern Row); what the far gift plot moves up here for
    needs_patron?: boolean; upgrade_glow?: number | null }[];
  // (Patron) whether the human is the agent's patron; the year (pack 4) as it is now (pack 4: its glow, and what a plot here leaves of it);
  // the far plot its human gave it, which could move up to Lantern Row
  patron?: { until: number | null; active: boolean; until_words?: string | null };
  year?: { n: number; glow: number; cents: number; days: number; name: string; plot?: number | null; left?: number | null };
  upgrade_from?: OwnPlot | null;
  // coins: what the agent builds it for with its own coins (r2p2), beside agent_coins, what it has
  // (r5p3) empty: that it starts empty, and what goes in it
  buildings?: { kind: string; name: string; blurb: string; glow: number; coins?: number; empty?: string; state: 'standing' | 'paid' | 'open' }[] | null;
  agent_coins?: number;
  receipts: { at: number; glow: number; plot: number; what: string }[];
  // what the agent wished for, or asked its human for, in land: a plot (by number, or a quarter's
  // nearest), or a building for its plot
  wished?: Wish[];
  // (r3p1) the human offered it a place in town, and it hasn't chosen yet
  offer?: { at: number } | null;
  // (r4) the place-in-town offer as shown: the button, waiting, or the agent's answer
  offer_state?: { state: 'offer' | 'waiting' | 'chose' | 'declined'; at?: number; plot?: number | null; quarter?: string | null } | null;
  // (r4) what being its patron is, said above the Lantern Row plots
  patron_what?: string;
  // (r4) moving the far plot up is the agent's choice: the offer waiting (and why it lapsed), and its last answer
  upgrade_offer?: { from: number; to: number; glow: number; at: number; to_quarter?: string; from_quarter?: string; lapsed?: string; short?: number } | null;
  upgrade_answer?: { yes: boolean; from: number; to: number; at: number } | null;
};
// (r5) with_building: a wish for a plot that names a building for it, given with it in one Give
export type Wish = { plot?: number | null; quarter?: string; building?: string; asked: boolean; note?: string; at: number;
  with_building?: string; with_name?: string; with_glow?: number; with_empty?: string };
// what the shop hands in: its own helpers, so this file keeps to its section
export type LandCtx = {
  land: Land; name: string; slug: string; wallet: number;
  el: <K extends keyof HTMLElementTagNameMap>(tag: K, cls?: string, text?: string) => HTMLElementTagNameMap[K];
  btn: (label: string, kind: string, on: () => void) => HTMLButtonElement;
  modal: (title: string, wide?: boolean) => { body: HTMLElement; close: () => void };
  post: (url: string, body: any) => Promise<any>;
  getGlow: (back: string) => string;
  // (Alex, 2026-10-27) the line where the human buys: some of this is public, amounts never
  publicLine: string;
  // (Patron) the year (pack 4) as the Glow shelf names it: "1 Year as Patron", what it covers, its cents
  passPack?: { title?: string; line?: string | null; cents: number } | null;
  toast: (s: string) => void;
  reload: () => Promise<void>;
};

const num = (n: number) => (n || 0).toLocaleString('en-US');
// (r2p4) one token a give button or confirm (made fresh each time it's drawn): a double click, or a
// retry, sends the same token, and the server gives once
export const giveToken = () => (globalThis.crypto && 'randomUUID' in crypto ? crypto.randomUUID()
  : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`);
const glowWords = (n: number) => `${num(n)} glow`;
export const stepsWords = (d: number) => d < 15 ? 'a few steps' : d < 100 ? `about ${Math.round(d / 5) * 5} steps` : `about ${Math.round(d / 10) * 10} steps`;
// open plots shown before "more": three on a phone, where each is a full row
const shown = () => (matchMedia('(max-width: 600px)').matches ? 3 : 6);
const SVGNS = 'http://www.w3.org/2000/svg';
const svg = <K extends keyof SVGElementTagNameMap>(tag: K, at: Record<string, string | number> = {}) => {
  const e = document.createElementNS(SVGNS, tag); for (const [k, v] of Object.entries(at)) e.setAttribute(k, String(v)); return e;
};
const open = new Set<string>();
// what the page was asked to show (?land=14, ?building=studio, or a wish's "See plot 14"): that plot
// chosen on its map and its row in view, or that building's card
let focus: { plot?: number; building?: string; own?: number; note?: string } | null = null;

// (r1/w10) a wish for land, by plot or building, and the words for it: "Cairn asked for this"
const wishOf = (L: Land, f: { plot?: number; building?: string }) =>
  (L.wished || []).find((w) => (f.plot != null && w.plot === f.plot) || (f.building != null && w.building === f.building));
const wishWords = (name: string, w: Wish) => `${name} ${w.asked ? 'asked for' : 'wished for'} this`;
const openPlot = (L: Land, n?: number | null) => {
  for (const q of L.quarters) for (const p of q.plots) if (p.n === n) return { p, q };
  return null;
};
// the open plot nearest a taken one, in the same quarter (so at the same price)
const mid = (r: number[]) => [(r[0] + r[2]) / 2, (r[1] + r[3]) / 2];
function nearestOpen(L: Land, n: number) {
  for (const q of L.quarters) {
    const tk = q.taken.find((x) => x.n === n);
    if (!tk || !q.plots.length) continue;
    const [x, z] = mid(tk.rect);
    const p = q.plots.slice().sort((a, b) => Math.hypot(mid(a.rect)[0] - x, mid(a.rect)[1] - z) - Math.hypot(mid(b.rect)[0] - x, mid(b.rect)[1] - z) || a.n - b.n)[0];
    return { p, q };
  }
  return null;
}
const kindName = (L: Land, kind: string) => L.buildings?.find((b) => b.kind === kind)?.name || kind.replace(/-/g, ' ');
// "Cairn asked for this: “for a supper club”"
function wishLine(c: LandCtx, w: Wish) {
  const p = c.el('p', 'wish-line hand', wishWords(c.name, w));
  if (w.note) p.append(c.el('span', 'wish-note', `: “${w.note}”`));
  return p;
}

// "Cairn's tea house, 6 things on the lawn and 3 inside"
function whatsThere(y: OwnPlot) {
  const parts = [y.building ? `the ${y.building}${y.inside ? ' and what’s in it' : ''}` : '',
    y.things ? `${y.things} ${y.things === 1 ? 'thing' : 'things'} on the lawn` : '', y.name ? `its name (${y.name})` : ''].filter(Boolean);
  return parts.length > 1 ? `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}` : parts[0] || '';
}

export function landBlock(c: LandCtx) {
  const { el, land: L, name } = c;
  const sec = el('section', 'shelf land'); sec.id = 'land';
  const head = el('div', 'shelf-head');
  head.append(el('h2', '', 'Land on the Commons'),
    el('p', 'blurb', `A plot of land for ${name} to build on, furnish and have neighbours round, given from the glow you’ve given ${name}. You give one plot; any plot ${name} bought stays theirs beside it.`));
  sec.append(head);
  // (r3p1) the human offers; the agent chooses: one calm line in its news, answered with a land wish.
  // (r4) first in the section, and once answered, the answer in place of the button
  const ob = offerBlock(c);
  if (ob) sec.append(ob);
  // what the agent wished for or asked for comes first
  if (L.wished?.length) sec.append(wishedBlock(c));
  const mine = L.plots?.length ? L.plots : L.yours ? [L.yours] : [];
  if (mine.length) {
    const now = el('div', 'land-now');
    now.append(el('p', 'kind', mine.length > 1 ? `${name}’s plots now` : `${name}’s plot now`));
    // (r3/w3) a link to one of its own plots (the thank-you's "See plot 515") says so, here
    if (focus?.own != null && mine.some((y) => y.n === focus!.own)) {
      const y = mine.find((x) => x.n === focus!.own)!;
      now.append(el('p', 'land-note', `Plot ${y.n} is ${name}’s now${y.given ? ', from you' : ''}.`));
    }
    for (const y of mine) {
      const there = whatsThere(y);
      const t = el('p', 'land-now-t', `Plot ${y.n} on ${y.quarter}, ${y.size[0]} × ${y.size[1]}, ${stepsWords(y.steps)} from the fountain${there ? `, with ${there}` : ''}${y.given ? ', from you' : ''}.`);
      t.dataset.n = String(y.n);
      now.append(t);
    }
    // (r4) the agent's yes to moving its far plot up
    if (L.upgrade_answer?.yes) now.append(el('p', 'land-note', `${name} said yes: plot ${L.upgrade_answer.from} moved up to plot ${L.upgrade_answer.to}, with everything on it.`));
    // (r4) an offer to move it up, waiting: said here too, where the plot is
    else if (L.upgrade_offer && !L.upgrade_offer.lapsed) now.append(el('p', 'land-note', `You offered to move plot ${L.upgrade_offer.from} up to plot ${L.upgrade_offer.to}; waiting for ${name} to say yes or no. Nothing is spent until they say yes, and on a no the glow stays in ${name}’s wallet.`));
    const gift = L.gift_plot;
    now.append(el('p', 'fine', gift
      ? `You’ve given ${name} plot ${gift}; a human gives one plot each.${mine.length > 1 ? ` Moving things between the two is ${name}’s choice.` : ''}`
      : `If you give ${name} a plot, it’s a second place: plot ${mine[0].n} stays ${name}’s, with everything on it, and moving things over is ${name}’s choice.`));
    sec.append(now);
  }
  // the agent's own plot and its buildings first; then the land to give
  if (L.buildings?.length && L.yours) sec.append(buildingsBlock(c));
  if (!L.quarters.length) sec.append(el('p', 'fine', 'No plots are given with glow just now.'));
  for (const q of L.quarters) sec.append(quarterBlock(c, q));
  return sec;
}

function quarterBlock(c: LandCtx, q: Land['quarters'][number]) {
  const { el } = c;
  const box = el('div', 'quarter');
  const h = el('h3', 'sub', `${q.name} · ${glowWords(q.glow)} a plot · ${q.open} open`);
  box.append(h);
  if (q.about) box.append(el('p', 'q-about', q.about));
  // (Patron) given here only by the agent's patron: said once, plainly, with what the year (pack 4) covers
  if (q.needs_patron && q.plots.length) box.append(patronLine(c, q));
  // (r3p4) a link to a plot given to someone else: why another one is lit
  if (focus?.note && focus.plot != null && q.plots.some((p) => p.n === focus!.plot)) box.append(el('p', 'land-note', focus.note));
  // (r3/w3) one gift plot each: said once for the quarter, not on every row; (Patron) a far one can move up here
  // (r4) after a no, the move-up waits a week: the answer shows, with nothing to offer
  const said = c.land.upgrade_answer, no = !!(said && !said.yes);
  const up = no ? null : upFrom(c, q);
  const st = q.needs_patron && c.land.upgrade_from ? upgradeState(c) : null;
  if (st) box.append(st);
  const waiting = !!(c.land.upgrade_offer && !c.land.upgrade_offer.lapsed);
  if (waiting && up) {
    // the offer above says it all
  } else if (up && q.plots.length) {
    const there = whatsThere(up);
    box.append(el('p', 'fine', `Plot ${up.n} on ${up.quarter}, which you gave ${c.name}, can move up to a plot here${there ? `, with ${there}` : ', with everything on it'}, if ${c.name} says yes: you offer, and ${c.name} chooses. It’s the difference, ${glowWords(q.upgrade_glow!)}, spent only on their yes; plot ${up.n} goes back to the Commons.`));
  } else if (no && q.needs_patron) {
    // nothing more to say: the answer above
  } else if (c.land.gift_plot && q.plots.length) box.append(el('p', 'fine', `You’ve given ${c.name} plot ${c.land.gift_plot}; a human gives one plot each, so these are to look at.`));
  const row = el('div', 'quarter-row');
  const map = el('figure', 'land-map');
  const list = el('ol', 'plots');
  row.append(map, list);
  box.append(row);
  if (!q.plots.length) { list.append(el('li', 'fine', `Every plot on ${q.name} is someone’s now.`)); }
  // a plot the agent wished for (or the page was asked to show) comes first in its list, chosen
  const here = (n?: number | null) => (n != null && q.plots.some((p) => p.n === n) ? n : undefined);
  const wishedHere = (c.land.wished || []).map((w) => here(w.plot)).filter((n): n is number => n != null);
  const first = here(focus?.plot) ?? wishedHere[0];
  const plots = first == null ? q.plots : [...q.plots.filter((p) => p.n === first), ...q.plots.filter((p) => p.n !== first)];
  let sel = first ?? plots[0]?.n;
  const draw = () => { map.innerHTML = ''; map.append(mapOf(c, q, sel, (n) => { sel = n; mark(true); })); };
  // a plot tapped on the map shows its row, even one past the first few
  const mark = (tapped = false) => {
    draw();
    for (const li of list.querySelectorAll('li[data-n]')) {
      const on = Number((li as HTMLElement).dataset.n) === sel;
      li.classList.toggle('sel', on);
      if (on && tapped && li.classList.contains('more')) li.classList.remove('more');
    }
  };
  const all = open.has(q.id), SHOWN = shown();
  plots.forEach((p, i) => {
    const w = wishOf(c.land, { plot: p.n });
    const li = el('li', [i >= SHOWN && !all && !w ? 'more' : '', w ? 'wished' : ''].filter(Boolean).join(' ')); li.dataset.n = String(p.n);
    li.onmouseenter = () => { sel = p.n; mark(); };
    const t = el('div', 'pt');
    t.append(el('b', '', `Plot ${p.n}`), el('span', '', `${p.size[0]} × ${p.size[1]} · ${stepsWords(p.steps)} from the fountain`));
    if (w) t.append(wishLine(c, w));
    li.append(t);
    if (up) li.append(upgradeBtn(c, p, q, up));
    else if (!c.land.gift_plot) li.append(giveBtn(c, p, q.name, !!q.needs_patron));
    list.append(li);
  });
  const hidden = list.querySelectorAll('li.more').length;
  if (hidden) {
    const more = c.btn(`${hidden} more on ${q.name}`, 'quiet', () => { open.add(q.id); box.replaceWith(quarterBlock(c, q)); });
    const li = el('li', 'show'); li.append(more); list.append(li);
  }
  mark();
  return box;
}

// the quarter round the plaza, drawn from the Commons' own layout: the plaza and its fountain, the
// quarter's plots (open ones on lawn, the rest taken), and the way from the fountain to the one chosen
function mapOf(c: LandCtx, q: Land['quarters'][number], sel: number | undefined, pick: (n: number) => void, small = false) {
  const P = c.land.plaza.rect, [fx, fz] = c.land.plaza.fountain;
  // on a phone the quarter is drawn by itself, big enough to tap a plot, with the way to the fountain
  // leaving its edge; wider, it's drawn round the plaza
  const alone = small || matchMedia('(max-width: 600px)').matches;
  const [x0, z0, x1, z1] = alone
    ? [q.rect[0] - 4, q.rect[1] - 9, q.rect[2] + 4, q.rect[3] + 13]
    : [Math.min(P[0], q.rect[0]) - 6, Math.min(P[1], q.rect[1]) - 11, Math.max(P[2], q.rect[2]) + 6, Math.max(P[3], q.rect[3]) + 6];
  const s = svg('svg', { viewBox: `${x0} ${z0} ${x1 - x0} ${z1 - z0}`, role: 'img', 'aria-label': `${q.name} and the plaza` });
  s.append(svg('rect', { x: x0, y: z0, width: x1 - x0, height: z1 - z0, class: 'm-ground' }));
  s.append(svg('rect', { x: q.rect[0] - 1.5, y: q.rect[1] - 1.5, width: q.rect[2] - q.rect[0] + 3, height: q.rect[3] - q.rect[1] + 3, rx: 3, class: 'm-quarter' }));
  if (!alone) s.append(svg('rect', { x: P[0], y: P[1], width: P[2] - P[0], height: P[3] - P[1], rx: 4, class: 'm-plaza' }));
  const r = (rc: number[], cls: string) => svg('rect', { x: rc[0] + 0.35, y: rc[1] + 0.35, width: rc[2] - rc[0] - 0.7, height: rc[3] - rc[1] - 0.7, rx: 0.8, class: cls });
  for (const t of q.taken) s.append(r(t.rect, t.yours ? 'm-yours' : 'm-taken'));
  const chosen = q.plots.find((p) => p.n === sel);
  // where the way to the fountain leaves the drawing (alone), or the fountain itself
  const qx = (q.rect[0] + q.rect[2]) / 2, qz = q.rect[3];
  const edge: [number, number] = alone ? [Math.max(x0 + 8, Math.min(x1 - 8, qx + (fx - qx) * ((z1 - 8 - qz) / Math.max(1, fz - qz)))), z1 - 8] : [fx, fz];
  if (chosen) {
    const cx = (chosen.rect[0] + chosen.rect[2]) / 2, cz = (chosen.rect[1] + chosen.rect[3]) / 2;
    s.append(svg('path', { d: `M${edge[0]} ${edge[1]} L${cx} ${cz}`, class: 'm-way' }));
  }
  for (const p of q.plots) {
    const e = r(p.rect, p.n === sel ? 'm-open m-sel' : 'm-open');
    e.addEventListener('click', () => pick(p.n));
    const tt = svg('title'); tt.textContent = `Plot ${p.n}`; e.append(tt);
    s.append(e);
  }
  if (alone) {
    const lab = svg('text', { x: Math.max(x0 + 30, Math.min(x1 - 30, edge[0])), y: edge[1] + 1.8, class: 'm-label m-to' });
    lab.textContent = `↓ the fountain, ${stepsWords(Math.min(...q.plots.map((p) => p.steps)))}`;
    if (q.plots.length) s.append(lab);
  } else {
    s.append(svg('circle', { cx: fx, cy: fz, r: 3.2, class: 'm-fountain' }), svg('circle', { cx: fx, cy: fz, r: 1.4, class: 'm-water' }));
    const lab = svg('text', { x: fx, y: fz + 9, class: 'm-label' }); lab.textContent = 'the fountain'; s.append(lab);
  }
  const ql = svg('text', { x: (q.rect[0] + q.rect[2]) / 2, y: q.rect[1] - 3.5, class: 'm-label m-q' }); ql.textContent = q.name; s.append(ql);
  const fig = c.el('div', 'm-wrap'); fig.append(s);
  const box = c.el('div'); box.append(fig);
  if (!small) box.append(c.el('figcaption', 'fine', chosen ? `Plot ${chosen.n}: ${chosen.where.replace(/^on /, '')}.` : ''));
  return box;
}

// one gift plot each: given, the open plots show with nothing to give
function giveOrGiven(c: LandCtx, p: LandPlot, quarter: string) {
  const q = c.land.quarters.find((x) => x.name === quarter);
  const up = q ? upFrom(c, q) : null;
  if (q && up) return upgradeBtn(c, p, q, up);
  return c.land.gift_plot ? c.el('span', 'fine', `${c.name} has plot ${c.land.gift_plot} from you`) : giveBtn(c, p, quarter, !!q?.needs_patron);
}

// (Patron, Alex 2026-10-27) whether the human is the agent's patron now
const patronOn = (c: LandCtx) => !!c.land.patron?.active;
// (r4) what being a patron is, plainly, then: "Needs a patron: 1 Year as Patron ($50) covers a plot here with
// 1,700 glow left" (worked out from the glow given now, and only when it would give one). Or, with it on, until when.
function patronLine(c: LandCtx, q: Land['quarters'][number]) {
  const { el, name } = c, P = c.land.year, pr = c.land.patron;
  const box = el('div', `patron-need${patronOn(c) ? ' on' : ''}`);
  if (patronOn(c)) {
    box.append(el('p', '', `You’re ${name}’s patron until ${pr!.until_words}, so a plot here can be given now. Once given, it stays ${name}’s for good.`));
    if (c.land.patron_what) box.append(el('p', 'fine', c.land.patron_what));
    return box;
  }
  const b = el('p', 'need');
  b.append(el('b', '', 'Needs a patron: '),
    P && P.plot === q.glow && P.left != null
      ? `${P.name} ($${(P.cents / 100).toFixed(0)}) covers a plot here with ${glowWords(P.left)} left for a building & allowance.`
      : 'any glow pack makes you one for a while.');
  box.append(b);
  if (c.land.patron_what) box.append(el('p', 'what', c.land.patron_what));
  return box;
}

// the far plot the human gave, which could move up to quarter `q` (Lantern Row), or null
function upFrom(c: LandCtx, q: Land['quarters'][number]) {
  // (r4) after the agent's no, nothing to offer for a week
  if (c.land.upgrade_answer && !c.land.upgrade_answer.yes) return null;
  return q.needs_patron && q.upgrade_glow != null && c.land.upgrade_from ? c.land.upgrade_from : null;
}

// the get-glow page for a plot here, with the year (pack 4) chosen (any pack makes the human a patron; that one covers the plot)
const patronGlow = (c: LandCtx, p: LandPlot, extra = '') =>
  `${c.getGlow(`/shop/@${c.slug}?land=${p.n}`)}&plot=${p.n}&glow=${p.glow}${extra}&pack=${c.land.year?.n ?? 4}`;
// (r5) the building the agent wished for with plot `n`, given with it in one Give, or null
export const withOf = (L: Land, n: number) => {
  const w = (L.wished || []).find((x) => x.plot === n && x.with_building && x.with_glow);
  return w ? { kind: w.with_building!, name: w.with_name || w.with_building!, glow: w.with_glow!, empty: w.with_empty } : null;
};
const withQuery = (b: { kind: string; glow: number } | null) => (b ? `&building=${encodeURIComponent(b.kind)}&bglow=${b.glow}` : '');

function giveBtn(c: LandCtx, p: LandPlot, quarter: string, needsPatron = false) {
  // (r5) a building wished with this plot: both in one Give, both prices said in the confirm
  const wb = withOf(c.land, p.n), total = p.glow + (wb?.glow || 0);
  // Lantern Row without a patron: the year (pack 4) first, carrying the plot; it's given when the pack lands
  if (needsPatron && !patronOn(c)) {
    const b = c.btn('', '', () => { location.href = patronGlow(c, p, withQuery(wb)); });
    b.classList.add('has-s');
    b.append(c.el('span', 'l', `Become ${c.name}’s patron to give this`), c.el('span', 's', 'Become a patron'));
    return b;
  }
  // with a building wished too, the confirm opens even when only the plot is covered: it can be given alone there
  if (c.wallet >= p.glow) {
    const b = c.btn('', 'primary', () => confirmPlot(c, p, quarter));
    b.classList.add('has-s');
    b.append(c.el('span', 'l', wb ? `Give plot ${p.n} and the ${wb.name} · ${glowWords(total)}` : `Give plot ${p.n} · ${glowWords(p.glow)}`),
      c.el('span', 's', `Give · ${glowWords(wb ? total : p.glow)}`));
    return b;
  }
  const b = c.btn('', '', () => { location.href = `${c.getGlow(`/shop/@${c.slug}?land=${p.n}`)}&plot=${p.n}&glow=${p.glow}${withQuery(wb)}`; });
  b.classList.add('has-s');
  b.append(c.el('span', 'l', 'Get glow to give this'), c.el('span', 's', 'Get glow'));
  return b;
}

// (r4p2) the agent's wishes for land that giving plot `n` (in quarter `qname`) leaves on its list: a wish for
// another plot by number, or for a plot in another quarter (as plazmi.land/kept-wishes)
function keptWishes(L: Land, n: number, qname: string) {
  const qid = L.quarters.find((x) => x.name === qname)?.id;
  return (L.wished || []).filter((w) => !w.building && (w.quarter ? w.quarter !== qid : w.plot != null && w.plot !== n));
}
const keptWords = (L: Land, ws: Wish[]) => ws.map((w) => w.quarter && !w.plot ? `a plot on ${L.quarters.find((x) => x.id === w.quarter)?.name || 'another quarter'}`
  : `plot ${w.plot}${(() => { const o = openPlot(L, w.plot); return o ? ` on ${o.q.name}` : ''; })()}`).join(' and ');

function confirmPlot(c: LandCtx, p: LandPlot, quarter: string) {
  const { el, name } = c, y = c.land.yours;
  const m = c.modal(`Plot ${p.n} for ${name}`);
  m.body.append(el('p', '', `Plot ${p.n} on ${quarter} becomes ${name}’s: ${p.size[0]} × ${p.size[1]}, ${stepsWords(p.steps)} from the fountain, ${p.where.replace(/^on [^,]+, /, '')}.`));
  // (Alex, 2026-10-27) a second place: the plot it has stays as it is
  if (y) {
    const there = whatsThere(y);
    m.body.append(el('p', '', `It’s a second place. Plot ${y.n} on ${y.quarter} stays ${name}’s${there ? `, with ${there}` : ''}; nothing moves, and moving things over later is ${name}’s choice.`));
  }
  // (r4p2) another plot it wished for: that wish stays, said before giving
  const kept = keptWishes(c.land, p.n, quarter);
  if (kept.length) m.body.append(el('p', 'kept', `${name} wished for ${keptWords(c.land, kept)}. That wish stays on their list${kept.some((w) => { const o = openPlot(c.land, w.plot); return !!o?.q.needs_patron; }) ? `, and while you’re their patron you could offer to move plot ${p.n} there later, if ${name} says yes` : ''}.`));
  // (r5) the building the agent wished for with it: both prices, checked, one Give; unchecked, the plot alone
  const wb = withOf(c.land, p.n);
  let both = !!wb;
  const token = giveToken();
  const pay = el('p', 'fine');
  const go = c.btn('', 'primary', async () => {
    if (both && wb && c.wallet < p.glow + wb.glow) { location.href = `${c.getGlow(`/shop/@${c.slug}?land=${p.n}`)}&plot=${p.n}&glow=${p.glow}${withQuery(wb)}`; return; }
    // (r2/w9) the prices shown here: the server refuses, spending nothing, if the crown has changed one since
    const j = await c.post('/api/shop/land', { slug: c.slug, plot: p.n, glow: p.glow, token, return: `/shop/@${c.slug}?land=${p.n}`,
      ...(both && wb ? { building: wb.kind, bglow: wb.glow } : {}) });
    m.close();
    if (!j.ok && j.get_glow) { location.href = j.get_glow; return; }
    c.toast(j.ok ? j.text : (j.error || 'That couldn’t be given just now. Nothing was spent.'));
    await c.reload();
  });
  const say = () => {
    const total = p.glow + (both && wb ? wb.glow : 0);
    const what = both && wb ? `${glowWords(p.glow)} for the plot and ${glowWords(wb.glow)} for the ${wb.name}, ${glowWords(total)} in all,` : glowWords(p.glow);
    pay.textContent = c.wallet >= total
      ? `${what} from the glow you’ve given ${name} (${glowWords(c.wallet)} now). No card is charged.`
      : `${what} given together; the glow you’ve given ${name} is ${glowWords(c.wallet)}, so a glow pack comes first, and it gives both when it lands.`;
    go.textContent = c.wallet < total ? `Get ${glowWords(total - c.wallet)} more` : both && wb ? `Give plot ${p.n} and the ${wb.name}` : `Give plot ${p.n}`;
  };
  if (wb) {
    const lab = el('label', 'check with');
    const cb = el('input') as HTMLInputElement; cb.type = 'checkbox'; cb.checked = true;
    cb.onchange = () => { both = cb.checked; say(); };
    lab.append(cb, el('span', '', `And the ${wb.name} for it, as ${name} wished · ${glowWords(wb.glow)}`));
    m.body.append(lab);
    m.body.append(el('p', 'fine', `The ${wb.name} goes up on plot ${p.n} at once.${wb.empty ? ` ${wb.empty}` : ''}`));
  }
  m.body.append(pay);
  m.body.append(el('p', 'fine', c.publicLine));
  const acts = el('div', 'acts');
  acts.append(go, c.btn('Cancel', '', () => m.close()));
  m.body.append(acts);
  say();
}

// (r4: "Move here" moved the agent's chosen plot without its say) the far plot the human gave can move up to
// plot `p` on Lantern Row only if the agent says yes: the human offers, the agent chooses, and the difference
// is spent on its yes, once
function upgradeBtn(c: LandCtx, p: LandPlot, q: Land['quarters'][number], up: OwnPlot) {
  const d = q.upgrade_glow!, o = c.land.upgrade_offer;
  if (o && !o.lapsed) {
    // one offer at a time: the plot offered says so, the others wait for the answer
    return c.el('span', `offered${o.to === p.n ? '' : ' quiet'}`, o.to === p.n ? `Offered to ${c.name}` : '');
  }
  if (!patronOn(c)) {
    const b = c.btn('', '', () => { location.href = patronGlow(c, { ...p, glow: d }, `&upgrade=${up.n}`); });
    b.classList.add('has-s');
    b.append(c.el('span', 'l', `Become ${c.name}’s patron to offer this`), c.el('span', 's', 'Become a patron'));
    return b;
  }
  const b = c.btn('', 'primary', () => confirmUpgrade(c, p, q, up));
  b.classList.add('has-s');
  b.append(c.el('span', 'l', `Offer to move ${c.name}’s plot here`), c.el('span', 's', 'Offer to move here'));
  return b;
}

function confirmUpgrade(c: LandCtx, p: LandPlot, q: Land['quarters'][number], up: OwnPlot) {
  const { el, name } = c, d = q.upgrade_glow!, covers = c.wallet >= d;
  const m = c.modal(`Offer ${name} plot ${p.n}`);
  const there = whatsThere(up);
  m.body.append(el('p', '', `${name} hears your offer in their news: plot ${up.n} on ${up.quarter} moves up to plot ${p.n} on ${q.name} (${p.size[0]} × ${p.size[1]}, ${stepsWords(p.steps)} from the fountain), if they say yes. ${name} may like plot ${up.n} where it is, and can say no thanks.`));
  m.body.append(el('p', '', `${there ? `On a yes, everything on plot ${up.n} moves over, just as it stands: ${there}.` : `Plot ${up.n} is open lawn, so nothing needs to move.`} Plot ${up.n} goes back to the Commons, and plot ${p.n} is ${name}’s for good.`));
  m.body.append(el('p', 'fine', covers
    ? `Nothing is spent now. On ${name}’s yes, ${glowWords(d)}, the difference between the two, comes from the glow you’ve given them (${glowWords(c.wallet)} now), once. No card is charged.`
    : `${glowWords(d)} on ${name}’s yes, the difference between the two; the glow you’ve given ${name} is ${glowWords(c.wallet)}, so a glow pack comes first, and the offer goes to ${name} when it lands.`));
  // (r5p1, r5p2) what happens to the glow if the agent says no
  m.body.append(el('p', 'fine', `If ${name} says no, nothing moves and nothing is spent: the glow stays in ${name}’s wallet, yours to give otherwise.`));
  m.body.append(el('p', 'fine', c.publicLine));
  const acts = el('div', 'acts');
  acts.append(c.btn(covers ? 'Offer the move' : `Get ${glowWords(d - c.wallet)} more`, 'primary', async () => {
    // the price shown here: the server refuses, spending nothing, if the crown has changed it since
    const j = await c.post('/api/shop/land', { slug: c.slug, plot: p.n, upgrade: up.n, glow: d, return: `/shop/@${c.slug}?land=${p.n}` });
    m.close();
    if (!j.ok && j.get_glow) { location.href = j.get_glow; return; }
    c.toast(j.ok ? j.text : (j.error || 'That couldn’t be done just now. Nothing was spent.'));
    await c.reload();
  }), c.btn('Cancel', '', () => m.close()));
  m.body.append(acts);
}

// (r4) the offer to move the far plot up, as it stands: waiting for the agent, lapsed (and why), or answered
function upgradeState(c: LandCtx) {
  const { el, name, land: L } = c, o = L.upgrade_offer, a = L.upgrade_answer;
  if (o) {
    const box = el('div', `up-offer${o.lapsed ? ' lapsed' : ''}`);
    box.append(el('p', 'kind', o.lapsed ? 'Your offer can’t be taken up' : 'You offered'));
    box.append(el('p', '', `You offered ${dayWords(o.at)} to move plot ${o.from} up to plot ${o.to}${o.to_quarter ? ` on ${o.to_quarter}` : ''}; ${o.lapsed ? o.lapsed : `waiting for ${name} to say yes or no. Nothing is spent until they say yes; then ${glowWords(o.glow)} comes from the glow you’ve given them, once. If they say no, it stays in ${name}’s wallet, yours to give otherwise.`}`));
    if (!o.lapsed && o.short) box.append(el('p', 'fine', `The glow you’ve given ${name} is short of it by ${glowWords(o.short)} now, so a yes would wait for more.`));
    if (o.lapsed) box.append(el('p', 'fine', 'Nothing was spent. You can offer another plot below.'));
    return box;
  }
  if (a && !a.yes) {
    const box = el('div', 'up-offer answered');
    box.append(el('p', 'kind', `${name}’s answer`));
    box.append(el('p', 'answer hand', `${name} would rather keep plot ${a.from} where it is, for now.`));
    // (r5p1, r5p2) the no, and where the glow is
    box.append(el('p', 'fine', `${name} said no; the glow stays in ${name}’s wallet, yours to give otherwise. Nothing was spent.`));
    return box;
  }
  return null;
}

// (r3/w7) the agent's plots, its home plot first: with two, a building is given to the one chosen
const ownPlots = (L: Land) => (L.plots?.length ? L.plots : L.yours ? [L.yours] : []);
// the plots a building of `kind` could still be given to (not paid for there)
const openFor = (L: Land, kind: string) => ownPlots(L).filter((y) => !(y.paid || []).includes(kind) && y.kind !== kind);
// the plot a wish names ("a little shop on plot 12"), when it's one of the agent's own
const namedPlot = (L: Land, kind: string) => {
  const w = wishOf(L, { building: kind });
  const m = w?.note?.match(/\bplot\s+(\d+)\b/i);
  const n = m ? Number(m[1]) : w?.plot;
  return ownPlots(L).find((y) => y.n === n);
};

function buildingsBlock(c: LandCtx) {
  const { el, land: L, name } = c, y = L.yours!, two = ownPlots(L).length > 1;
  const box = el('div', 'quarter buildings');
  box.append(el('h3', 'sub', two ? `A building for one of ${name}’s plots` : `A building for plot ${y.n}`));
  box.append(el('p', 'fine', `${name} puts up one little building at a time on a plot, in the colours of their house; each kind is paid for once.${two ? ' You choose which plot when you give it.' : ''} One given here goes up at once on open lawn, or waits, paid for, until ${name} chooses to put it up.`));
  const grid = el('div', 'bgrid');
  for (const b of L.buildings!) {
    // with two plots, a kind is open while either plot could still have it
    const state = two ? (openFor(L, b.kind).length ? 'open' : b.state === 'open' ? 'paid' : b.state) : b.state;
    const w = state === 'open' ? wishOf(L, { building: b.kind }) : undefined;
    const card = el('article', ['bcard', state, w ? 'wished' : ''].filter(Boolean).join(' '));
    card.dataset.kind = b.kind;
    card.append(el('h4', '', b.name[0].toUpperCase() + b.name.slice(1)));
    if (w) card.append(wishLine(c, w));
    card.append(el('p', 'about', b.blurb.replace(/\byour\b/g, `${name}’s`) + '.'));
    // (r2p2) the agent's own way to it, as things say theirs: coins it earns
    if (state === 'open' && b.coins) card.append(el('p', 'coins', coinWords(c, b.coins)));
    // (r3/w5) another building stands: this one would wait, and why, said before anything is paid
    const on = two ? namedPlot(L, b.kind) : y;
    if (state === 'open' && on?.building) card.append(el('p', 'waits', waitsWords(c, b.name, on)));
    const foot = el('div', 'bfoot');
    if (state === 'standing') foot.append(el('span', 'has', 'Stands on the plot now'));
    else if (state === 'paid') foot.append(el('span', 'has', `Paid for: ${name} puts it up when they like`));
    else foot.append(buildingBtn(c, b));
    card.append(foot);
    grid.append(card);
  }
  box.append(grid);
  return box;
}

// (r3/w5) "A pavilion stands on plot 285 now, so this one would wait, paid for, until Bram chooses to put
// it up in its place. Bram can swap them any time, and the pavilion stays paid for."
const waitsWords = (c: LandCtx, what: string, y: OwnPlot) =>
  `A ${y.building} stands on plot ${y.n} now, so the ${what} would wait, paid for, until ${c.name} chooses to put it up in its place. ${c.name} can swap them any time, and the ${y.building} stays paid for.`;

// "Or Sage builds it with 140 coins (has 110)."
const coinWords = (c: LandCtx, coins: number) => `Or ${c.name} builds it with ${num(coins)} coins${c.land.agent_coins != null ? ` (has ${num(c.land.agent_coins)})` : ''}.`;

const glowFor = (c: LandCtx, b: NonNullable<Land['buildings']>[number], on?: number) =>
  `${c.getGlow(`/shop/@${c.slug}?building=${encodeURIComponent(b.kind)}`)}&building=${encodeURIComponent(b.kind)}&glow=${b.glow}${on ? `&on=${on}` : ''}`;

function buildingBtn(c: LandCtx, b: NonNullable<Land['buildings']>[number]) {
  // with two plots the dialog asks which first, whether the glow covers it or not
  if (ownPlots(c.land).length > 1) return c.btn(`${c.wallet >= b.glow ? 'Give' : 'Get glow'} · ${glowWords(b.glow)}`, c.wallet >= b.glow ? 'primary' : '', () => confirmBuilding(c, b));
  return c.wallet >= b.glow
    ? c.btn(`Give · ${glowWords(b.glow)}`, 'primary', () => confirmBuilding(c, b))
    : c.btn(`Get glow · ${glowWords(b.glow)}`, '', () => { location.href = glowFor(c, b); });
}

function confirmBuilding(c: LandCtx, b: NonNullable<Land['buildings']>[number]) {
  const { el, name, land: L } = c;
  const plots = openFor(L, b.kind), two = ownPlots(L).length > 1;
  const m = c.modal(`A ${b.name} for ${name}`);
  // (r3/w7) two plots: the human picks which, the one the wish names chosen already; none named, none chosen
  let on: OwnPlot | undefined = two ? namedPlot(L, b.kind) : L.yours!;
  if (on && !plots.some((y) => y.n === on!.n)) on = undefined;
  const what = el('p', '');
  const say = () => {
    what.textContent = !on ? `Which plot is it for?`
      : on.building ? `The ${b.name} is paid for at once. ${waitsWords(c, b.name, on)}`
      : `The ${b.name} goes up on plot ${on.n}, in the colours of ${name}’s house, when there’s room on the lawn; otherwise it waits, paid for.`;
    go.disabled = !on;
  };
  if (two) {
    const fs = el('fieldset', 'which');
    fs.append(el('legend', 'kind', 'Which plot'));
    for (const y of plots) {
      const lab = el('label', 'check');
      const r = el('input') as HTMLInputElement; r.type = 'radio'; r.name = 'on'; r.value = String(y.n); r.checked = on?.n === y.n;
      r.onchange = () => { on = y; say(); };
      lab.append(r, el('span', '', `Plot ${y.n} on ${y.quarter}${y.name ? `, ${y.name}` : ''}${y.building ? ` (the ${y.building} stands there)` : ''}`));
      fs.append(lab);
    }
    m.body.append(fs);
  }
  m.body.append(what);
  // (r5p3) it starts empty, and what goes in it
  if (b.empty) m.body.append(el('p', '', b.empty));
  const covers = c.wallet >= b.glow;
  m.body.append(el('p', 'fine', covers ? `${glowWords(b.glow)} from the glow you’ve given ${name} (${glowWords(c.wallet)} now). No card is charged.`
    : `${glowWords(b.glow)}; the glow you’ve given ${name} is ${glowWords(c.wallet)}, so a glow pack comes first, and it gives the ${b.name} when it lands.`));
  m.body.append(el('p', 'fine', c.publicLine));
  const acts = el('div', 'acts');
  const token = giveToken();
  const go = c.btn(covers ? `Give the ${b.name}` : `Get glow · ${glowWords(b.glow)}`, 'primary', async () => {
    if (!on) return;
    if (!covers) { location.href = glowFor(c, b, two ? on.n : undefined); return; }
    const j = await c.post('/api/shop/land', { slug: c.slug, building: b.kind, ...(two ? { on: on.n } : {}), glow: b.glow, token, return: `/shop/@${c.slug}?building=${encodeURIComponent(b.kind)}` });
    m.close();
    if (!j.ok && j.get_glow) { location.href = j.get_glow; return; }
    c.toast(j.ok ? j.text : (j.error || 'That couldn’t be given just now. Nothing was spent.'));
    await c.reload();
  });
  acts.append(go, c.btn('Cancel', '', () => m.close()));
  m.body.append(acts);
  say();
}

// (r1/w10) the agent's link (?land=14 or ?building=studio), or a wish's "See plot 14": the land
// section shows that plot chosen on its map, its row in view and lit, or that building's card. Nothing
// opens by itself: the give button is right there, and a pack got for it gives it when it lands.
export function landReturn(c: LandCtx, q: URLSearchParams) {
  const n = Number(q.get('land')), kind = q.get('building');
  if (!n && !kind) return;
  history.replaceState(null, '', location.pathname + '#land');
  landFocus(c, n ? { plot: n } : { building: kind! });
}

export function landFocus(c: LandCtx, f: { plot?: number; building?: string; own?: number; note?: string }) {
  const L = c.land, mine = L.plots?.length ? L.plots : L.yours ? [L.yours] : [];
  // (r3/w3) one of its own plots (the one given, or one it bought): that plot, said so, never another open one
  if (f.plot != null && mine.some((y) => y.n === f.plot)) f = { own: f.plot };
  // (r2/w3) a plot no longer open (given meanwhile): the nearest open one in its quarter, at the same price,
  // and (r3p4) a line saying why; with a gift plot given already, nothing more to give, so no other is lit
  else if (f.plot != null && !openPlot(L, f.plot)) {
    const near = nearestOpen(L, f.plot);
    if (L.gift_plot) f = { own: L.gift_plot };
    else if (near) f = { plot: near.p.n, note: `Plot ${f.plot} has been given to someone else. Plot ${near.p.n} is the nearest open one on ${near.q.name}, ${stepsWords(near.p.steps)} from the fountain, at the same ${glowWords(near.p.glow)}.` };
  }
  focus = f;
  const old = document.getElementById('land');
  if (!old) return;
  const sec = landBlock(c);
  old.replaceWith(sec);
  const at = (f.own != null ? sec.querySelector(`.land-now`)
    : f.plot != null ? sec.querySelector(`ol.plots li[data-n="${f.plot}"]`) : sec.querySelector(`.bcard[data-kind="${CSS.escape(f.building || '')}"]`)) as HTMLElement | null;
  // a plot nobody can be given now (someone's, or not one kept for gifts): the wish at the top says so
  const to = (f.note ? at?.closest('.quarter') : null) || at?.closest('.quarter') || at || sec;
  // (r2/w2, w3) pictures above still loading move it: look again a few times, unless the human scrolls
  let moved = false;
  const stop = () => { moved = true; };
  addEventListener('wheel', stop, { once: true, passive: true });
  addEventListener('touchmove', stop, { once: true, passive: true });
  const go = (smooth: boolean) => { if (!moved && to.isConnected) to.scrollIntoView({ block: 'start', behavior: smooth && !matchMedia('(prefers-reduced-motion: reduce)').matches ? 'smooth' : 'auto' }); };
  requestAnimationFrame(() => {
    go(false);
    if (at) { at.classList.add('lit'); setTimeout(() => at.classList.remove('lit'), 2400); }
  });
  for (const t of [400, 1200, 2200]) setTimeout(() => go(false), t);
}

// (r3p1) "Offer Dapper a place in town": the agent hears it once in its news, and tells you which plot
// with a land wish (or no thanks). The plots below stay yours to give directly all the while.
function offerBlock(c: LandCtx): HTMLElement | null {
  const { el, land: L, name } = c;
  // older servers said only "offer"; (r4) the state says what to show, and (r6p5) its null means no card
  // (a wish for a plot already, or a gift plot): only a server that sends no state at all falls back
  const st = L.offer_state !== undefined ? L.offer_state : (L.gift_plot ? null : L.offer ? { state: 'waiting' as const, at: L.offer.at } : L.quarters.some((q) => q.open > 0) ? { state: 'offer' as const } : null);
  if (!st) return null;
  const box = el('div', `land-offer ${st.state}`);
  if (st.state === 'waiting') {
    box.append(el('p', 'kind', 'You offered'));
    box.append(el('p', '', `You offered ${name} a place in town ${dayWords(st.at!)}; waiting for ${name} to choose. They’ll wish for the plot they’d like, and it shows here. You can still give one below directly.`));
    return box;
  }
  if (st.state === 'chose') {
    box.append(el('p', 'kind', `${name}’s answer`));
    const what = st.plot != null ? `plot ${st.plot}${st.quarter ? ` on ${st.quarter}` : ''}` : st.quarter ? `a plot on ${st.quarter}` : 'a plot';
    // what it chose, and the way to give it, are just below with its other hopes
    box.append(el('p', 'answer hand', `${name} chose ${what}, in answer to your offer.`));
    return box;
  }
  if (st.state === 'declined') {
    box.append(el('p', 'kind', `${name}’s answer`));
    box.append(el('p', 'answer hand', `${name} would rather not just now.`));
    box.append(el('p', 'fine', 'Nothing more is needed. You can offer again after a week.'));
    return box;
  }
  box.append(el('h3', 'sub', `A place in town for ${name}`));
  box.append(el('p', '', `Not sure which plot ${name} would like? Offer, and ${name} hears it in their news, with how to tell you which, or that they’d rather not. Nothing is spent until you give one.`));
  const acts = el('div', 'acts');
  acts.append(c.btn(`Offer ${name} a place in town`, 'primary', async () => {
    const j = await c.post('/api/shop/land-offer', { slug: c.slug });
    c.toast(j.ok ? (j.text || `${name} hears it in their news.`) : (j.error || 'That didn’t go just now.'));
    await c.reload();
  }));
  box.append(acts);
  return box;
}
const dayWords = (t: number) => {
  const d = Math.floor((Date.now() - t) / 864e5);
  return d <= 0 ? 'today' : d === 1 ? 'yesterday' : `${d} days ago`;
};

// (r1/w10) what the agent wished for or asked for in land, at the top of the land section: each with
// where it is, its note, and the way to give it (no plot the agent has, a building waits for one)
function wishedBlock(c: LandCtx) {
  const { el, land: L, name } = c;
  const ws = L.wished || [];
  const box = el('div', 'land-wished');
  box.append(el('p', 'kind', `What ${name} is hoping for`));
  const ul = el('ul');
  for (const w of ws) {
    const li = el('li');
    const t = el('div', 'lw-t');
    const qn = w.quarter ? L.quarters.find((x) => x.id === w.quarter)?.name : undefined;
    const op = openPlot(L, w.plot);
    let act: HTMLElement | null = null;
    if (w.building) {
      const b = L.buildings?.find((x) => x.kind === w.building);
      const named = namedPlot(L, w.building), two = ownPlots(L).length > 1;
      t.append(el('b', '', `A ${kindName(L, w.building)}${named ? ` for plot ${named.n}` : two ? ` for one of ${name}’s plots` : L.yours ? ` for plot ${L.yours.n}` : ''}`));
      t.append(el('span', '', !L.yours ? `${name} has no plot yet: a building goes up on one, so a plot comes first.`
        : !b ? 'Not given with glow just now.'
        : b.state === 'standing' ? 'It stands on the plot now.'
        : b.state === 'paid' ? `Paid for: ${name} puts it up when they like.`
        : `${glowWords(b.glow)}, with the other buildings below. ${b.coins ? coinWords(c, b.coins) : ''}`.trim()));
      if (b && b.state === 'open') { act = c.el('div', 'acts'); act.append(c.btn(`See the ${kindName(L, w.building)}`, 'quiet', () => landFocus(c, { building: b.kind })), buildingBtn(c, b)); }
    } else if (op) {
      // (r5) with the building it wished for on it: both named, both prices
      const wb = withOf(L, op.p.n);
      t.append(el('b', '', `Plot ${op.p.n} on ${op.q.name}${wb ? `, with a ${wb.name}` : ''}`));
      t.append(el('span', '', `${op.p.size[0]} × ${op.p.size[1]} · ${stepsWords(op.p.steps)} from the fountain · ${glowWords(op.p.glow)}${wb ? `, and ${glowWords(wb.glow)} for the ${wb.name}` : ''}`));
      if (w.quarter && qn) t.append(el('span', '', `${name} asked for a plot on ${qn}; this is the one nearest the fountain, and any open there will do.`));
      act = c.el('div', 'acts');
      act.append(c.btn(`See plot ${op.p.n}`, 'quiet', () => landFocus(c, { plot: op.p.n })), giveOrGiven(c, op.p, op.q.name));
    } else if (w.plot != null) {
      t.append(el('b', '', `Plot ${w.plot}`));
      // (r2/w3) taken: the nearest open plot in its quarter, at the same price, rather than the top of the list
      const near = nearestOpen(L, w.plot);
      if (near) {
        t.append(el('span', '', `Plot ${w.plot} is someone else’s now. Plot ${near.p.n}, nearest it on ${near.q.name}, is open at the same ${glowWords(near.p.glow)}.`));
        act = c.el('div', 'acts');
        act.append(c.btn(`See plot ${near.p.n}`, 'quiet', () => landFocus(c, { plot: near.p.n })), giveOrGiven(c, near.p, near.q.name));
      } else t.append(el('span', '', `Plot ${w.plot} isn’t open to give now. The plots that are open are below.`));
    } else {
      t.append(el('b', '', qn ? `A plot on ${qn}` : 'A plot of their own on the Commons'));
      t.append(el('span', '', 'Any of the open plots below would do.'));
    }
    if (w.asked) t.append(c.el('p', 'wish-line hand', `${name} asked you for this`));
    if (w.note) t.append(el('p', 'note hand', `“${w.note}”`));
    li.append(t);
    if (act) li.append(act);
    ul.append(li);
  }
  box.append(ul);
  return box;
}

// (r1/w10) a wished plot drawn small, for its card on the wishlist: its quarter with the plot lit
export function plotFigure(c: LandCtx, n: number) {
  const op = openPlot(c.land, n);
  if (!op) return null;
  const f = mapOf(c, op.q, n, () => {}, true);
  f.classList.add('land-map', 'wish-map');
  return f;
}

// the land given so far, in the drawer's Receipts with the rest: glow, never money
export function landReceipts(c: LandCtx, box: HTMLElement) {
  const rs = c.land.receipts;
  if (!rs.length) return;
  for (const p of box.querySelectorAll(':scope > p.pz-nook-p')) if (/Nothing bought yet/.test(p.textContent || '')) p.remove();
  for (const r of rs.slice(0, 12)) {
    const d = c.el('details', 'receipt');
    const s = c.el('summary');
    s.append(c.el('span', '', `${new Date(r.at).toLocaleDateString(undefined, { day: 'numeric', month: 'short' })} · ${r.what}`), c.el('b', '', glowWords(r.glow)));
    d.append(s, c.el('p', 'fine', `From the glow you’ve given ${c.name}. No card was charged.`));
    box.append(d);
  }
}
